export function normCnpj(s) {
  return String(s || "").replace(/\D/g, "");
}

export function formatarCnpj(cnpj) {
  const d = normCnpj(cnpj);
  if (d.length !== 14) return cnpj || "—";
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
}

/** Máscara CPF 000.000.000-00 (protótipo eSocial lançamentos). */
export function formatarCpf(cpf) {
  const d = String(cpf || "").replace(/\D/g, "");
  if (d.length !== 11) return d || "—";
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
}

/** TXM-418 — únicos códigos com rótulo amigável na listagem; os demais continuam exibindo o código bruto. */
const ROTULOS_AMIGAVEIS_COD_RELATORIO = {
  ICMS_APURACAO: "ICMS",
};

/** Valor de `cod_relatorio` retornado pela API no campo `tipo`. */
export function exibirCodRelatorio(codigo) {
  const s = String(codigo ?? "").trim();
  if (!s) return "—";
  return ROTULOS_AMIGAVEIS_COD_RELATORIO[s] || s;
}

export function pad2(n) {
  return n < 10 ? `0${n}` : String(n);
}

export function fmtMesAno(iso) {
  if (!iso || iso.length !== 7) return "—";
  const [y, m] = iso.split("-");
  return `${m}/${y}`;
}

export function parseYM(iso) {
  if (!iso || iso.length !== 7) return null;
  const [y, mo] = iso.split("-");
  const year = parseInt(y, 10);
  const month = parseInt(mo, 10);
  if (Number.isNaN(year) || Number.isNaN(month)) return null;
  return new Date(year, month - 1, 1);
}

export function getPeriodoISO(mes, ano) {
  if (!mes || !ano) return "";
  return `${ano}-${mes}`;
}

export function isOrdemPeriodoValida(periodoIni, periodoFim) {
  if (periodoIni.length !== 7 || periodoFim.length !== 7) return false;
  const dIni = parseYM(periodoIni);
  const dFim = parseYM(periodoFim);
  if (!dIni || !dFim) return false;
  return dIni.getTime() <= dFim.getTime();
}

export function isInicioMaisDe5AnosAtras(periodoIni) {
  const competencia = parseYM(periodoIni);
  if (!competencia) return false;
  const limite = new Date();
  limite.setFullYear(limite.getFullYear() - 5);
  return competencia < limite;
}

export const MSG_COMPETENCIA_FUTURA =
  "Não é permitido selecionar competência posterior ao mês atual.";

/** Competência YYYY-MM posterior ao mês atual (horário local). */
export function isCompetenciaFutura(periodoYm) {
  const competencia = parseYM(periodoYm);
  if (!competencia) return false;
  const agora = new Date();
  const mesAtual = new Date(agora.getFullYear(), agora.getMonth(), 1);
  return competencia.getTime() > mesAtual.getTime();
}

/** Verdadeiro se início ou fim for competência futura. */
export function periodoTemCompetenciaFutura(periodoIni, periodoFim) {
  return isCompetenciaFutura(periodoIni) || isCompetenciaFutura(periodoFim);
}

export function mesclarPeriodos(iniA, fimA, iniB, fimB) {
  const datas = [iniA, fimA, iniB, fimB].map(parseYM).filter(Boolean);
  if (!datas.length) return { ini: iniA, fim: fimA };
  datas.sort((a, b) => a.getTime() - b.getTime());
  const min = datas[0];
  const max = datas[datas.length - 1];
  return {
    ini: `${min.getFullYear()}-${pad2(min.getMonth() + 1)}`,
    fim: `${max.getFullYear()}-${pad2(max.getMonth() + 1)}`,
  };
}

export function periodoPadraoAposFim(fimIso) {
  const d = parseYM(fimIso);
  if (!d) {
    const agora = new Date();
    const iso = `${agora.getFullYear()}-${pad2(agora.getMonth() + 1)}`;
    return { ini: iso, fim: iso };
  }
  d.setMonth(d.getMonth() + 1);
  const iso = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
  return { ini: iso, fim: iso };
}

export function periodoPadraoAtual() {
  const agora = new Date();
  const iso = `${agora.getFullYear()}-${pad2(agora.getMonth() + 1)}`;
  return { ini: iso, fim: iso };
}

/** Competência YYYY-MM → Date (primeiro dia do mês). */
export function dateFromPeriodoISO(iso) {
  return parseYM(iso);
}

/** Date → competência YYYY-MM. */
export function periodoISOFromDate(date) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;
}

export function periodoPadraoAtualDates() {
  const agora = new Date();
  const d = new Date(agora.getFullYear(), agora.getMonth(), 1);
  return { dtInicial: d, dtFinal: new Date(d.getTime()) };
}

export function splitPeriodoISO(iso) {
  if (!iso || iso.length !== 7) return { mes: "", ano: "" };
  const [ano, mes] = iso.split("-");
  return { mes, ano };
}

export function listarCompetenciasPeriodo(iniIso, fimIso) {
  const ini = parseYM(iniIso);
  const fim = parseYM(fimIso);
  if (!ini || !fim || ini.getTime() > fim.getTime()) return [];
  const lista = [];
  const d = new Date(ini.getTime());
  while (d.getTime() <= fim.getTime()) {
    lista.push(`${d.getFullYear()}-${pad2(d.getMonth() + 1)}`);
    d.setMonth(d.getMonth() + 1);
  }
  return lista;
}

export function competenciasAdicionalSobrepostas(addIni, addFim, atualIni, atualFim) {
  const atualSet = {};
  listarCompetenciasPeriodo(atualIni, atualFim).forEach((c) => {
    atualSet[c] = true;
  });
  return listarCompetenciasPeriodo(addIni, addFim).filter((c) => atualSet[c]);
}

export function resumirPeriodoCompetencias(competencias) {
  if (!competencias?.length) return "";
  const ini = competencias[0];
  const fim = competencias[competencias.length - 1];
  if (ini === fim) return fmtMesAno(ini);
  return `${fmtMesAno(ini)} – ${fmtMesAno(fim)}`;
}

/** TXM-325 item 08 — anos civis cobertos por um período (YYYY-MM), em ordem crescente. */
export function anosNoPeriodo(iniIso, fimIso) {
  const anos = new Set();
  for (const c of listarCompetenciasPeriodo(iniIso, fimIso)) {
    anos.add(Number(c.slice(0, 4)));
  }
  return [...anos].sort((a, b) => a - b);
}

/** TXM-418 — a mensagem de "sumarização indisponível" (ver carregarContextoSumarizacao no
 * backend, apuracoes-sumarizacao.service.ts) embute o status real no texto: "(status:
 * pendente|processando|erro)". Pendente/processando é só a apuração ainda não ter terminado
 * de processar — diferente de erro, não deveria alarmar com o mesmo tom vermelho de falha. */
export function sumarizacaoEmAndamento(mensagem) {
  return /status:\s*(pendente|processando)/i.test(mensagem || "");
}

const ECD_SIGLAS_MAIUSCULAS = new Set([
  "icms", "ipi", "iss", "pis", "cofins", "irrf", "inss", "fgts",
  "ipva", "iptu", "iof", "csll", "irpj", "cte",
]);
const ECD_CONECTIVOS_MINUSCULOS = new Set([
  "a", "à", "ao", "aos", "as", "o", "os", "de", "da", "do", "das", "dos",
  "e", "em", "com", "para", "por", "sem", "s",
]);

/** TXM-598 — descrição de conta contábil (ECD) vem sempre em CAIXA ALTA do mock/dado real;
 * o protótipo mostra Title Case ("ICMS a Recuperar", "Custo das Mercadorias Vendidas") —
 * sigla tributária conhecida fica maiúscula, conectivo português minúsculo (exceto 1ª
 * palavra), resto capitalizado. Só reformata o TEXTO — não mexe no código da conta (ver
 * comentário em AbaEcd.js sobre por que o código não é pontuado). */
export function formatarDescricaoConta(descricao) {
  if (!descricao) return descricao;
  let primeiraPalavraVista = false;
  return descricao
    .trim()
    .toLowerCase()
    .split(/(\s+)/)
    .map((token) => {
      const somenteLetras = token.replace(/[^\p{L}]/gu, "");
      if (!somenteLetras) return token;
      const ehPrimeira = !primeiraPalavraVista;
      primeiraPalavraVista = true;
      if (ECD_SIGLAS_MAIUSCULAS.has(somenteLetras)) return token.toUpperCase();
      if (!ehPrimeira && ECD_CONECTIVOS_MINUSCULOS.has(somenteLetras)) return token;
      return token.replace(/\p{L}/u, (c) => c.toUpperCase());
    })
    .join("");
}

export function gerarAnosSelect() {
  const agora = new Date();
  const yMax = agora.getFullYear() + 1;
  const yMin = agora.getFullYear() - 20;
  const anos = [];
  for (let y = yMax; y >= yMin; y--) {
    anos.push(String(y));
  }
  return anos;
}
